import type { DraftOwner } from "./wizard-draft-ownership";

/**
 * One client UUID per draft ownership generation.
 * Retries after a lost insert response reuse that id. A discard, a switch to
 * another draft, or an org change mints or selects a different id.
 *
 * Two tabs editing one resumed draft still last-write-wins. This helper does
 * not add a version check.
 */

export interface WizardDraftRouteRequest {
  orgId: string | null;
  /** Draft id in the URL now. Null means the route has no draft query. */
  urlDraftId: string | null;
  /**
   * Draft id the previous route observation had in the URL.
   * Null while this generation was minted as a blank wizard and has not been written to the URL.
   */
  previousUrlDraftId: string | null;
  forceNew?: boolean;
}

export function resolveWizardDraftOwnership(
  current: DraftOwner,
  request: WizardDraftRouteRequest,
  mint: () => string,
): { owner: DraftOwner; changed: boolean } {
  const forceNew = request.forceNew === true;
  if (request.orgId === null) {
    if (!forceNew && current.orgId === null) return { owner: current, changed: false };
    return { owner: { generation: current.generation + 1, orgId: null, draftId: null }, changed: true };
  }
  if (forceNew) {
    return { owner: { generation: current.generation + 1, orgId: request.orgId, draftId: mint() }, changed: true };
  }
  if (request.urlDraftId) {
    if (current.orgId === request.orgId && current.draftId === request.urlDraftId) {
      return { owner: current, changed: false };
    }
    return {
      owner: { generation: current.generation + 1, orgId: request.orgId, draftId: request.urlDraftId },
      changed: true,
    };
  }
  // The URL used to name a draft and now it does not. That is a new wizard, not a retry of the old one.
  if (request.previousUrlDraftId) {
    return { owner: { generation: current.generation + 1, orgId: request.orgId, draftId: mint() }, changed: true };
  }
  if (current.orgId === request.orgId && current.draftId) {
    return { owner: current, changed: false };
  }
  if (current.orgId !== request.orgId) {
    return { owner: { generation: current.generation + 1, orgId: request.orgId, draftId: mint() }, changed: true };
  }
  return { owner: { generation: current.generation + 1, orgId: request.orgId, draftId: mint() }, changed: true };
}

export interface WizardDraftIdentityRow {
  id: string;
  orgId: string;
  createdBy: string;
  payload: string;
}

/**
 * Same rules as upsert_product_wizard_draft after migration 0250.
 * A caller-supplied id inserts once, then updates that row. Another org or
 * creator cannot take the row over.
 */
export function applyIdempotentWizardDraftUpsert(
  rows: WizardDraftIdentityRow[],
  caller: { orgId: string; userId: string; allowed: boolean },
  draftId: string,
  payload: string,
): string {
  if (!caller.allowed) throw new Error("Not authorized");
  const existing = rows.find((row) => row.id === draftId);
  if (!existing) {
    rows.push({ id: draftId, orgId: caller.orgId, createdBy: caller.userId, payload });
    return draftId;
  }
  if (existing.orgId !== caller.orgId || existing.createdBy !== caller.userId) {
    throw new Error("Draft not found");
  }
  existing.payload = payload;
  return existing.id;
}
